'use client';

/**
 * SignatureFlip37 - Die Signatur-Animation des Funnels: eine grosse,
 * dreidimensional schwebende "37"-Coin dreht sich um die Y-Achse (540deg),
 * blitzt im Wendepunkt lime auf und enthuellt auf der Rueckseite eine
 * Vorschau des Crew-Pass. Dies ist die "Geburtsstunde" des Pass - der Moment,
 * in dem aus der Marken-Zahl die persoenliche Akte wird.
 *
 * Quellen (Showpiece-Prototypen):
 *   docs/prototypes/signature_37_zu_crewpass.html     - Timing, Flash-Choreographie
 *   docs/prototypes/crewpass_unlock.html              - Coin-Spin (.spin/.idle/flash)
 *   docs/prototypes/landing_37_dreht_nach_rechts.html - Gleiche Coin-Mechanik
 *
 * Tokens aus layout.tsx:
 *   --motion-coin (1.15s)   Dauer der Drehung
 *   --ease-expo             cubic-bezier(.5,0,.2,1) - gewichtsbetont
 *   --brand-green / --accent - Lime-Flash und Gradient-Stops
 *   --g37a / --g37b         - 37-Gradient (#3BB6A0 -> #1E8A52)
 *   --font-brand            - Space Grotesk 700
 *
 * API:
 *   Steuerung wahlweise imperativ (`ref.current.triggerSpin()`) oder
 *   deklarativ (`autoStart`). Nach voller Animation feuert `onComplete`.
 *   prefers-reduced-motion: kein Spin, sofort Rueckseite zeigen.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';

export interface SignatureFlip37Handle {
  /** Startet die Spin-Animation. Mehrfach-Aufrufe waehrend einer laufenden
   *  Drehung werden ignoriert. Nach Abschluss feuert onComplete. */
  triggerSpin: () => void;
  /** Setzt die Coin auf die Vorderseite (37) zurueck. */
  reset: () => void;
}

export interface SignatureFlip37Props {
  /** Callback, sobald die Drehung abgeschlossen ist (1.15s nach Start)
   *  und die Coin im idle-Floaty-Loop ist. */
  onComplete?: () => void;
  /** Wenn true, startet die Drehung automatisch beim Mount. */
  autoStart?: boolean;
  /** Aussere Breite/Hoehe der Buehne in px. Default 360x300 wie im Prototyp
   *  crewpass_unlock.html. */
  size?: number;
  /** Inhalt der Rueckseite - typischerweise eine MiniaturCrewPass-Komponente. */
  crewPassPreview?: ReactNode;
}

const SPIN_MS = 1150;

const SignatureFlip37 = forwardRef<SignatureFlip37Handle, SignatureFlip37Props>(
  function SignatureFlip37(
    { onComplete, autoStart = false, size = 360, crewPassPreview },
    ref
  ) {
    const sceneRef = useRef<HTMLDivElement | null>(null);
    const flashRef = useRef<HTMLDivElement | null>(null);
    const completeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [phase, setPhase] = useState<'front' | 'spinning' | 'idle'>('front');
    const [reducedMotion, setReducedMotion] = useState(false);

    // prefers-reduced-motion erkennen. Wir lesen das Media-Query EINMAL beim
    // Mount und horchen auf Aenderungen - Nutzer kann die Einstellung im OS
    // umschalten, ohne den Tab neu zu laden.
    useEffect(() => {
      if (typeof window === 'undefined') return;
      const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      const update = () => setReducedMotion(mq.matches);
      update();
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    }, []);

    const triggerSpin = useCallback(() => {
      // Doppelklick-Schutz: laufende Drehung nicht neu starten.
      if (phase === 'spinning') return;

      // Reduced-Motion: kein Spin, kein Flash - direkt auf die Rueckseite
      // springen und onComplete sofort feuern (im naechsten Tick, damit
      // Listener nicht synchron im Setter-Aufruf landen).
      if (reducedMotion) {
        setPhase('idle');
        const t = setTimeout(() => onComplete?.(), 0);
        completeTimerRef.current = t;
        return;
      }

      // Flash-Reset via reflow-Trick (siehe Prototyp: void offsetWidth),
      // damit die Keyframe-Animation neu startet, wenn triggerSpin nach
      // einem reset() erneut gerufen wird.
      const flash = flashRef.current;
      if (flash) {
        flash.classList.remove('s37-flash-go');
        // Erzwingt einen Reflow, damit der Browser den Animation-State neu
        // berechnet - ohne diesen Trick lebt die alte Animation weiter und
        // ein zweiter Spin zeigt keinen Lime-Blitz mehr.
        void flash.offsetWidth;
        flash.classList.add('s37-flash-go');
      }

      setPhase('spinning');
      const t = setTimeout(() => {
        setPhase('idle');
        onComplete?.();
      }, SPIN_MS);
      completeTimerRef.current = t;
    }, [onComplete, phase, reducedMotion]);

    const reset = useCallback(() => {
      if (completeTimerRef.current) {
        clearTimeout(completeTimerRef.current);
        completeTimerRef.current = null;
      }
      flashRef.current?.classList.remove('s37-flash-go');
      setPhase('front');
    }, []);

    useImperativeHandle(ref, () => ({ triggerSpin, reset }), [triggerSpin, reset]);

    // autoStart: nach Mount EINMAL spinnen. Wir warten ein animation-frame,
    // damit der Reflow-Trick im Flash bei Server->Client-Hydrate sauber
    // greift (Element ist garantiert im Layout).
    useEffect(() => {
      if (!autoStart) return;
      const raf = requestAnimationFrame(() => triggerSpin());
      return () => cancelAnimationFrame(raf);
      // triggerSpin als Dep absichtlich weggelassen - autoStart soll nur
      // beim ersten Mount feuern, nicht bei jeder triggerSpin-Identitaets-
      // aenderung (sonst startet die Animation in einer Schleife neu).
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoStart]);

    useEffect(() => {
      return () => {
        if (completeTimerRef.current) clearTimeout(completeTimerRef.current);
      };
    }, []);

    // Glare-Effekt: pointermove ueber die Buehne uebersetzt Cursor-Position
    // in CSS-Custom-Properties (--s37-mx/--s37-my), die der Glare-Layer als
    // radial-gradient-Origin nutzt. Nur aktiv, wenn die Rueckseite sichtbar
    // ist (sonst zeigt das Highlight ueber der 37, was sinnlos waere).
    const handlePointerMove = useCallback(
      (e: ReactPointerEvent<HTMLDivElement>) => {
        if (phase !== 'idle') return;
        const el = sceneRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const x = ((e.clientX - r.left) / r.width) * 100;
        const y = ((e.clientY - r.top) / r.height) * 100;
        el.style.setProperty('--s37-mx', `${x}%`);
        el.style.setProperty('--s37-my', `${y}%`);
      },
      [phase]
    );

    // 300px Default-Hoehe entspricht dem Showpiece crewpass_unlock.html.
    // size = 360 -> 300 (5:6-Verhaeltnis). Wer eine andere Breite vorgibt,
    // behaelt das Verhaeltnis automatisch.
    const height = Math.round((size * 300) / 360);

    return (
      <div
        ref={sceneRef}
        className="s37-scene"
        onPointerMove={handlePointerMove}
        style={{ width: size, height, ['--s37-size' as string]: `${size}px` }}
      >
        <div ref={flashRef} className="s37-flash" aria-hidden="true" />
        <div
          className={`s37-coin ${phase === 'spinning' ? 's37-spin' : ''} ${phase === 'idle' ? 's37-idle' : ''}`}
        >
          <div className="s37-face s37-front" aria-hidden={phase === 'idle'}>
            <span className="s37-num">37</span>
          </div>
          <div className="s37-face s37-back" aria-hidden={phase !== 'idle'}>
            <div className="s37-glare" aria-hidden="true" />
            {crewPassPreview}
          </div>
        </div>

        <style jsx>{`
          .s37-scene {
            position: relative;
            perspective: 1500px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .s37-flash {
            position: absolute;
            inset: -50px;
            z-index: 8;
            border-radius: 50%;
            pointer-events: none;
            opacity: 0;
            background: radial-gradient(
              circle,
              rgba(200, 245, 66, 0.85),
              rgba(19, 178, 101, 0.2) 40%,
              transparent 65%
            );
          }
          .s37-flash.s37-flash-go {
            animation: s37-flash var(--motion-coin, 1.15s) ease-out;
          }
          @keyframes s37-flash {
            0%,
            42% {
              opacity: 0;
            }
            52% {
              opacity: 0.9;
            }
            72%,
            100% {
              opacity: 0;
            }
          }

          .s37-coin {
            position: relative;
            width: 100%;
            height: 100%;
            transform-style: preserve-3d;
            transition:
              transform var(--motion-coin, 1.15s)
                var(--ease-expo, cubic-bezier(0.5, 0, 0.2, 1));
          }
          .s37-coin.s37-spin {
            transform: rotateY(540deg);
          }
          .s37-coin.s37-idle {
            transform: rotateY(540deg);
            animation: s37-floaty 7s ease-in-out infinite;
          }
          @keyframes s37-floaty {
            0%,
            100% {
              transform: rotateY(540deg) translateY(0);
            }
            50% {
              transform: rotateY(540deg) translateY(-12px);
            }
          }

          .s37-face {
            position: absolute;
            inset: 0;
            backface-visibility: hidden;
            -webkit-backface-visibility: hidden;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .s37-front {
            flex-direction: column;
          }
          .s37-num {
            font-family: var(--font-brand, system-ui, sans-serif);
            font-weight: 700;
            font-size: clamp(170px, calc(var(--s37-size, 360px) * 0.58), 210px);
            line-height: 1;
            letter-spacing: -0.05em;
            background: linear-gradient(
              155deg,
              var(--g37a, #1fe085),
              var(--g37b, #0c7a45)
            );
            -webkit-background-clip: text;
            background-clip: text;
            -webkit-text-fill-color: transparent;
            color: transparent;
            filter: drop-shadow(0 16px 36px rgba(19, 178, 101, 0.4));
            user-select: none;
          }

          .s37-back {
            transform: rotateY(180deg);
          }

          /* Glare auf der Rueckseite: folgt dem Cursor via --s37-mx/--s37-my
             (default 30%/18% wie im Prototyp). Wird nur sichtbar, wenn die
             Buehne aktiv gehovert wird - ergaenzt die "lebendige Karte". */
          .s37-glare {
            position: absolute;
            inset: 0;
            z-index: 6;
            pointer-events: none;
            border-radius: 22px;
            background: radial-gradient(
              320px circle at var(--s37-mx, 30%) var(--s37-my, 18%),
              rgba(255, 255, 255, 0.22),
              transparent 45%
            );
            opacity: 0;
            transition: opacity 0.2s;
          }
          .s37-scene:hover .s37-glare {
            opacity: 0.6;
          }

          @media (prefers-reduced-motion: reduce) {
            .s37-coin,
            .s37-coin.s37-spin,
            .s37-coin.s37-idle {
              transition: none;
              animation: none;
              transform: rotateY(180deg);
            }
            .s37-flash,
            .s37-flash.s37-flash-go {
              animation: none;
              opacity: 0;
            }
          }
        `}</style>
      </div>
    );
  }
);

export default SignatureFlip37;
